import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { useMyTeamRole } from '../../../team/useMyTeamRole';
import { stateMessages as messages } from './stateMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the live screens; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Live.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SendingStoppedStateProps {
  mailbox: string | null;
  waiting: number;
}

/**
 * Sending stopped (design t6): the mailbox needs a reconnect. The owner reconnects in Settings →
 * Outreach; a member reads who can do it. The waiting count is real (`waitingCount`).
 */
const SendingStoppedStateComponent: React.FC<SendingStoppedStateProps> = ({ mailbox, waiting }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const isOwner = useMyTeamRole() === 'OWNER';
  return (
    <section
      className={clsx(styles.card, styles.state, styles.stateStopped)}
      data-testid="outreach-state-stopped"
      role="alert"
    >
      <div className={shared.row}>
        <span className={clsx(shared.eyebrow, 'text-danger')}>{intl.formatMessage(messages.stoppedEyebrow)}</span>
        <span className={clsx(shared.chip, shared.chipNeg, 'ms-auto')}>{intl.formatMessage(messages.stoppedChip)}</span>
      </div>
      <h2 className={styles.stateTitle}>{intl.formatMessage(messages.stoppedTitle, { count: waiting })}</h2>
      <p className={styles.stateBody}>
        {mailbox === null
          ? intl.formatMessage(messages.stoppedBodyNoMailbox)
          : intl.formatMessage(messages.stoppedBody, { mailbox })}
      </p>
      {isOwner ? (
        <div className={styles.stateActions}>
          <SMLButton color="primary" omitCap onClick={() => router.push(SMLRoutes.settings, { tab: 'outreach' })}>
            {intl.formatMessage(messages.reconnect)}
          </SMLButton>
        </div>
      ) : (
        <div className={styles.inset} role="note" data-testid="outreach-state-stopped-member">
          <span className={shared.small}>{intl.formatMessage(messages.memberNote)}</span>
        </div>
      )}
    </section>
  );
};

export const SendingStoppedState = TypedMemo(SendingStoppedStateComponent);
